import DOMPurify from 'dompurify';

const PURIFY = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'strike',
    'del',
    'ul',
    'ol',
    'li',
    'a',
    'span',
    'div',
    'blockquote',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'data-list'],
};

/**
 * Strip BOM, trim, and decode common HTML-entity-encoded payloads once.
 */
export function normalizeNoteContentForDisplay(raw) {
  let s = String(raw ?? '');
  s = s.replace(/^\uFEFF/, '').trim();
  if (typeof document !== 'undefined' && (s.includes('&lt;') || /&#\d+;|&#[xX][0-9a-fA-F]+;/.test(s))) {
    const t = document.createElement('textarea');
    t.innerHTML = s;
    s = (t.value || '').replace(/^\uFEFF/, '').trim();
  }
  return s;
}

/**
 * Strip tags to plain text (browser). Used for empty checks.
 */
export function stripHtmlToText(html) {
  if (html == null || html === '') return '';
  if (typeof document === 'undefined') {
    return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const d = document.createElement('div');
  d.innerHTML = html;
  return (d.textContent || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isEditorContentEmpty(html) {
  return stripHtmlToText(html).length === 0;
}

/**
 * Sanitize stored HTML for display (XSS-safe). Keeps Quill list markup.
 */
export function sanitizeNoteHtml(html) {
  const normalized = normalizeNoteContentForDisplay(html);
  return DOMPurify.sanitize(normalized, PURIFY);
}

/**
 * True when content should render as HTML (rich notes). Legacy plain notes stay text.
 */
export function isLikelyRichHtml(raw) {
  const n = normalizeNoteContentForDisplay(raw);
  if (!n) return false;
  if (/^<[a-z]/i.test(n)) return true;
  if (/<(p|ul|ol|li|strong|em|b|i|u|br|blockquote|span|div)\b/i.test(n)) return true;
  return false;
}

// Notes saved through the `panel` NotesTab variant used to carry a hidden
// marker prepended to `content`, splitting one shared backend note list
// into "call notes" and "panel notes" purely on the frontend. That split
// has been removed — both variants show every note again — but notes
// saved while it was active still have this marker baked into their
// stored content. `stripPanelNoteMarker` is kept solely to clean that up
// on display/edit, so those older notes don't carry a stray empty
// `<span>` around; nothing writes the marker anymore.
const PANEL_NOTE_MARKER = '<span data-note-kind="diet"></span>';

export function stripPanelNoteMarker(content) {
  const s = String(content ?? '');
  const trimmed = s.trimStart();
  return trimmed.startsWith(PANEL_NOTE_MARKER)
    ? trimmed.slice(PANEL_NOTE_MARKER.length)
    : s;
}

/**
 * Wrap legacy plain-text notes so Quill can edit them without losing line breaks.
 */
export function plainTextToQuillHtml(text) {
  const s = text == null ? '' : String(text);
  const escaped = s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const withBreaks = escaped.split('\n').join('<br/>');
  if (!withBreaks) return '<p><br></p>';
  return `<p>${withBreaks}</p>`;
}
